import { SquareArrowLeft, SquareArrowRight } from "lucide-react";
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";

import { useCachedState } from "src/hooks/useCache";
import { useModalStackStore } from "src/stores/modalStackStore";
import { cn } from "src/utilities/shadcnUtils";
import Popup, { type PopupProps } from "../Popup";
import XButton from "../XButton";

export interface ModalBoxProps extends Omit<PopupProps, "children"> {
  width?: number | string;
  children: Array<ReactNode | boolean> | ReactNode | boolean;
  closeButton?: boolean | ReactNode;
  closeOnClickBackdrop?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  shovable?: boolean;
  closeButtonClassName?: string;
  zIndex?: number;
  transparent?: boolean;
  /** Keep the application Save pill usable above this modal. */
  allowPendingChanges?: boolean;
}

const ModalBox = ({
  children,
  width,
  closeButton = true,
  closeOnClickBackdrop = !!closeButton,
  closeOnEscape = !!closeButton,
  position,
  shovable,
  closeButtonClassName,
  zIndex: zIndexProp,
  open,
  onClose,
  transparent = false,
  allowPendingChanges = false,
  ...props
}: ModalBoxProps) => {
  const modalId = useId();
  const registerModal = useModalStackStore((s) => s.registerModal);
  const unregisterModal = useModalStackStore((s) => s.unregisterModal);
  const [stackZIndex, setStackZIndex] = useState<number | undefined>();

  useEffect(() => {
    if (open) {
      const assigned = registerModal(modalId, onClose, closeOnEscape, allowPendingChanges);
      setStackZIndex(assigned);
      return () => unregisterModal(modalId);
    } else {
      setStackZIndex(undefined);
    }
  }, [open, modalId, onClose, closeOnEscape, allowPendingChanges, registerModal, unregisterModal]);

  const effectiveZIndex = stackZIndex ?? zIndexProp;

  const [shoved, setShoved] = useCachedState(false, "modal-shoved");
  const itIsShoved = shoved && shovable;

  const handleShove = useCallback(() => {
    setShoved((prev) => !prev);
  }, []);
  return (
    <Popup
      nested
      {...props}
      overlayStyle={{
        left: itIsShoved ? "auto" : 0,
        zIndex: effectiveZIndex,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(2px)",
      }}
      contentStyle={{
        width,
        zIndex: effectiveZIndex,
        backgroundColor: transparent ? "transparent" : "hsl(var(--card))",
        color: transparent ? undefined : "hsl(var(--card-foreground))",
      }}
      open={open}
      onClose={onClose}
      closeOnDocumentClick={closeOnClickBackdrop}
      closeOnEscape={false}
    >
      {/*// @ts-ignore */}
      {(close: () => void) => (
        <>
          {Children.map(children, (child) => {
            if (!isValidElement(child)) return child;
            // Only inject custom props into component children — forwarding them to a
            // host element (e.g. <div>) makes React try to write `shoved`/`onClose` to the DOM.
            if (typeof child.type === "string") return child;
            return cloneElement(child, { onClose: close, shoved: itIsShoved } as any);
          })}
          {closeButton === true ? (
            <XButton
              className={cn(
                "text-muted-foreground/70 bg-transparent z-10 absolute top-[18px] right-[18px]",
                closeButtonClassName
              )}
              onClick={close}
            />
          ) : closeButton === false ? null : (
            closeButton
          )}
          {shovable ? (
            itIsShoved ? (
              <SquareArrowLeft
                onClick={handleShove}
                className="absolute left-2 top-1/2 text-green cursor-pointer fill-green-200 z-10"
              />
            ) : (
              <SquareArrowRight
                onClick={handleShove}
                className="absolute right-2 top-1/2 text-green cursor-pointer fill-green-200 z-10"
              />
            )
          ) : null}
        </>
      )}
    </Popup>
  );
};

export default ModalBox;
